/**
 * Decided during render, never from an effect — same rule as the POS. An effect-driven redirect
 * paints the protected screen for a frame and pushes two history entries.
 */

import { Navigate, Outlet, useLocation } from 'react-router';
import Box from '@mui/material/Box';

import { selectStatus } from '@pos/shared';
import { logo } from '@pos/tokens';

import { useAppSelector } from '@/app/hooks';
import { DashboardShell } from '@/features/shell';
import { DashboardTour } from '@/features/tour';

export function ProtectedRoute() {
  const status = useAppSelector(selectStatus);
  const location = useLocation();

  if (status === 'restoring') {
    return (
      <Box
        sx={{
          display: 'grid',
          placeItems: 'center',
          minHeight: '100dvh',
          bgcolor: 'var(--pos-surface-base)',
        }}
      >
        {/* The product's mark, not a shop's: nobody is signed in yet, so there is no shop to
            have a look. Read from the token catalogue rather than written out here, per the
            resale rule in `brand.ts`. */}
        <Box component="img" src={logo.mark} alt="" sx={{ width: 48, opacity: 0.4 }} />
      </Box>
    );
  }

  if (status === 'revoked') return <Navigate to="/device-revoked" replace />;

  if (status !== 'active') {
    // No `/unlock` destination here — the dashboard has no PIN step, only `/sign-in`. `status`
    // can still report `'locked'` (the shared session machinery is POS's too), it just never
    // routes anywhere dashboard-specific for it.
    const attempted = `${location.pathname}${location.search}`;
    return <Navigate to="/sign-in" replace state={{ from: attempted }} />;
  }

  return (
    <DashboardShell>
      <Outlet />
      {/* Here rather than in the shell: only a signed-in person on a real page gets the tour —
          never the sign-in or "Continue as" screens, which this guard sends elsewhere. */}
      <DashboardTour />
    </DashboardShell>
  );
}
